import { useCallback, useMemo } from 'react';
import FilterDropdown from '../filtering/FilterDropdown';
import {
    AppointmentsEntryButton,
    MoreFiltersMenu,
    type MoreMenuCategory,
    type FilterOption
} from '@odo/shared-ui';
import { useToolbarFold } from './useToolbarFold';
import { useFilterOptions } from '../../hooks/useFilterOptions';
import { useManufacturerStatusOptions } from '../../hooks/useWorkQueue';
import { useAdminAppointmentsUpcomingCount } from '../../hooks/useAdminAppointments';
import { humanizeManufacturerStatus } from './util';
import { practiceNameOptionsFromList } from '../../utils/practiceFilterOptions';
import { usePracticeOptions } from '../../hooks/useBilling';
import { SearchDropdown } from './SearchDropdown';
import { type WorkQueueCase, type StatusMeta } from '../../services/workQueue';
import { SplitByPriorityToggle } from './priority/SplitByPriorityToggle';

// ─── Filter categories ────────────────────────────────────────────────────────

const TAGS_OPTIONS = ['V1', 'V2'] as const;
const MONITORING_OPTIONS = ['Not started', 'Started', 'Paused', 'Stopped'] as const;
const PRIORITY_OPTIONS = ['Extreme', 'High', 'Normal'] as const;

/** Keys of categories that are always visible in the bar, in left-to-right order. */
const PINNED_KEYS = ['team', 'assignee', 'priority'] as const;
type PinnedKey = (typeof PINNED_KEYS)[number];

/** Fixed footprint of the appointments icon inside the measured filter row:
 *  w-10 (40px) + gap-2 (8px) + mr-1 (4px). Passed to the fold hook so the
 *  pills don't get handed the icon's space. */
const APPOINTMENTS_BUTTON_RESERVED_PX = 52;

/** Keys that live in More by default; auto-promote to bar when activated. */
const OVERFLOW_KEYS = ['practice', 'tags', 'monitoring', 'manufacturerStatus'] as const;
type OverflowKey = (typeof OVERFLOW_KEYS)[number];

type FilterKey = PinnedKey | OverflowKey;

// ─── Props ────────────────────────────────────────────────────────────────────

export interface WorkQueueToolbarProps {
    statusLookup: Map<string, StatusMeta>;
    onSearchSelect: (result: WorkQueueCase) => void;
    practiceFilter: string[];
    onPracticeFilter: (selected: string[]) => void;
    assigneeOptions: readonly FilterOption[];
    assigneeFilter: string[];
    onAssigneeFilter: (selected: string[]) => void;
    teamFilter: string[];
    onTeamFilter: (selected: string[]) => void;
    tagsFilter: string[];
    onTagsFilter: (selected: string[]) => void;
    monitoringFilter: string[];
    onMonitoringFilter: (selected: string[]) => void;
    manufacturerStatusFilter: string[];
    onManufacturerStatusFilter: (selected: string[]) => void;
    priorityFilter: string[];
    onPriorityFilter: (selected: string[]) => void;
    splitByPriority: boolean;
    onSplitByPriority: (on: boolean) => void;
    onOpenAppointments: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function WorkQueueToolbar({
    statusLookup,
    onSearchSelect,
    practiceFilter,
    onPracticeFilter,
    assigneeOptions,
    assigneeFilter,
    onAssigneeFilter,
    teamFilter,
    onTeamFilter,
    tagsFilter,
    onTagsFilter,
    monitoringFilter,
    onMonitoringFilter,
    manufacturerStatusFilter,
    onManufacturerStatusFilter,
    priorityFilter,
    onPriorityFilter,
    splitByPriority,
    onSplitByPriority,
    onOpenAppointments
}: WorkQueueToolbarProps) {
    const { filterOptions } = useFilterOptions();
    // Distinct portal-synced values from the DB — the option list grows on its
    // own as new manufacturer statuses appear; nothing is hardcoded.
    const { data: manufacturerStatusOptions } = useManufacturerStatusOptions();
    const upcomingAppointmentsCount = useAdminAppointmentsUpcomingCount();

    // Values stay practice NAMES: they live in the URL and in saved views, so
    // an id migration would break both. Duplicate names merge their doctors.
    const { practices } = usePracticeOptions();
    const practiceOptions = useMemo(() => practiceNameOptionsFromList(practices), [practices]);

    // Manufacturer statuses display sentence-cased but must round-trip to the
    // backend as the raw UPPER_SNAKE values (humanization is lossy) — so the
    // dropdown shows labels and this map converts a selection back to raw.
    const manufacturerStatusLabelToRaw = useMemo(() => {
        const map = new Map<string, string>();
        for (const raw of manufacturerStatusOptions ?? []) {
            map.set(humanizeManufacturerStatus(raw), raw);
        }
        return map;
    }, [manufacturerStatusOptions]);
    const manufacturerStatusDisplayOptions = useMemo(
        () => (manufacturerStatusOptions ?? []).map(humanizeManufacturerStatus),
        [manufacturerStatusOptions]
    );
    const handleManufacturerStatusChange = useCallback(
        (labels: string[]) => {
            onManufacturerStatusFilter(labels.map((l) => manufacturerStatusLabelToRaw.get(l) ?? l));
        },
        [onManufacturerStatusFilter, manufacturerStatusLabelToRaw]
    );

    // Map filter keys to their current values (used to derive active state and
    // to initialise promoted set).
    const filterValues: Record<FilterKey, string[]> = {
        assignee: assigneeFilter,
        practice: practiceFilter,
        priority: priorityFilter,
        team: teamFilter,
        tags: tagsFilter,
        monitoring: monitoringFilter,
        // Display values — the URL/backend keep the raw UPPER_SNAKE strings.
        manufacturerStatus: manufacturerStatusFilter.map(humanizeManufacturerStatus)
    };

    // ── Bar button list ───────────────────────────────────────────────────────
    // Pinned only. More categories stay inside More even while active — their
    // state reads as the dark count badge on the More button. Auto-promotion
    // (filters popping into the bar on activation) was tried and rejected:
    // buttons appearing/sticking in the bar felt unpredictable.
    const barKeys: FilterKey[] = [...PINNED_KEYS];

    // Active flags for the fold hook (active buttons survive folding longest).
    const barActiveFlags: boolean[] = barKeys.map((k) => filterValues[k].length > 0);

    // ── Responsive fold ───────────────────────────────────────────────────────
    // The hook returns a set of indices to fold into More. We measure each
    // button's intrinsic scrollWidth against container width — see hook for the
    // oscillation-safety reasoning. The appointments icon lives inside the
    // measured container but isn't a foldable button, so its footprint
    // (w-10 = 40px + gap-2 = 8px + mr-1 = 4px) is reserved up front.
    const { foldedSet, containerRef, setButtonEl } = useToolbarFold(
        barKeys.length,
        barActiveFlags,
        APPOINTMENTS_BUTTON_RESERVED_PX
    );

    // Collect which keys actually fold (are in More due to responsive overflow).
    const responsiveFoldedKeys = new Set(barKeys.filter((_, i) => foldedSet.has(i)));

    // The toggle node is stable as long as the callback identity is stable.
    // buildCategory is recreated on every render (it's a closure), so we pass
    // the toggle as a node built once at the call site. Declared BEFORE the
    // moreCategories/allCategories maps below: buildCategory closes over this
    // const, and calling it before the initializer runs is a TDZ ReferenceError.
    const splitToggleNode = (
        <SplitByPriorityToggle on={splitByPriority} onToggle={onSplitByPriority} />
    );

    // More menu receives: all overflow keys + any responsively-folded bar keys.
    const moreCategories: MoreMenuCategory[] = [
        ...OVERFLOW_KEYS,
        ...barKeys.filter((k) => responsiveFoldedKeys.has(k))
    ].map((k) => buildCategory(k));

    // Every category, for the More menu's open value view — a just-promoted
    // category must keep resolving there or its list/search unmounts mid-use.
    const allCategories: MoreMenuCategory[] = (
        [...PINNED_KEYS, ...OVERFLOW_KEYS] as FilterKey[]
    ).map((k) => buildCategory(k));

    // Hidden active count for the More badge. Includes categories that use
    // forceActive (e.g. Priority when split-by-priority is on).
    const hiddenActiveCount = moreCategories.filter(
        (c) => c.selectedOptions.length > 0 || (c.forceActive ?? false)
    ).length;

    // ── Option maps ──────────────────────────────────────────────────────────

    function buildCategory(key: FilterKey): MoreMenuCategory {
        const configs: Record<
            FilterKey,
            { label: string; options: readonly FilterOption[]; onChange: (s: string[]) => void }
        > = {
            assignee: { label: 'Assignee', options: assigneeOptions, onChange: onAssigneeFilter },
            team: { label: 'Team', options: filterOptions.teams, onChange: onTeamFilter },
            priority: { label: 'Priority', options: PRIORITY_OPTIONS, onChange: onPriorityFilter },
            practice: { label: 'Practice', options: practiceOptions, onChange: onPracticeFilter },
            tags: { label: 'Tags', options: TAGS_OPTIONS, onChange: onTagsFilter },
            monitoring: {
                label: 'Monitoring',
                options: MONITORING_OPTIONS,
                onChange: onMonitoringFilter
            },
            manufacturerStatus: {
                label: 'Manufacturer status',
                options: manufacturerStatusDisplayOptions,
                onChange: handleManufacturerStatusChange
            }
        };
        const cfg = configs[key];
        const isPriority = key === 'priority';
        return {
            key,
            label: cfg.label,
            options: cfg.options,
            selectedOptions: filterValues[key],
            onChange: cfg.onChange,
            footer: isPriority ? splitToggleNode : undefined,
            forceActive: isPriority ? splitByPriority : undefined
        };
    }

    return (
        <div className="flex items-center gap-3 px-4 pb-3 min-w-0">
            {/* Search — typeahead dropdown that focuses a case via ?case= deep-link */}
            <SearchDropdown statusLookup={statusLookup} onSelect={onSearchSelect} />

            {/* Filter button row — flex-1 + min-w-0 makes this the constrained,
          measurable space the fold hook compares button widths against.
          (A content-sized flex-shrink-0 container would always equal the sum
          of its children and the fold would never trigger.) justify-end keeps
          the buttons pinned right, replacing the old spacer div. */}
            <div className="flex flex-1 min-w-0 items-center justify-end gap-2" ref={containerRef}>
                {/* Appointments entry — leads the right-aligned cluster, left of
                    the filter pills. Not a foldable button: the fold hook
                    reserves its width via APPOINTMENTS_BUTTON_RESERVED_PX. */}
                <AppointmentsEntryButton
                    upcomingCount={upcomingAppointmentsCount}
                    onClick={onOpenAppointments}
                    className="relative grid place-items-center mr-1 h-10 w-10 rounded-full border-2 border-gray-300 shadow-sm bg-white text-gray-700 hover:bg-gray-50 transition-colors shrink-0"
                    iconClassName="h-5 w-5"
                />

                {barKeys.map((key, i) => {
                    if (foldedSet.has(i)) return null;

                    const cat = buildCategory(key);

                    return (
                        <FilterDropdownButton
                            key={key}
                            index={i}
                            category={cat}
                            setButtonEl={setButtonEl}
                        />
                    );
                })}

                {/* More button — always rendered (some categories always live there) */}
                <MoreFiltersMenu
                    categories={moreCategories}
                    allCategories={allCategories}
                    hiddenActiveCount={hiddenActiveCount}
                />
            </div>
        </div>
    );
}

// ─── FilterDropdownButton ─────────────────────────────────────────────────────
// Thin wrapper: renders a FilterDropdown in workQueueVariant mode and
// forwards the button DOM ref to the fold hook via a ref callback.

interface FilterDropdownButtonProps {
    index: number;
    category: MoreMenuCategory;
    setButtonEl: (index: number, el: HTMLButtonElement | null) => void;
}

function FilterDropdownButton({ index, category, setButtonEl }: FilterDropdownButtonProps) {
    // FilterDropdown doesn't expose a buttonRef prop, so we wrap it in a span
    // and query the button once the span mounts. The callback is memoized so
    // React doesn't run a null → reattach cycle on every render (an inline
    // function gets a new identity each time, forcing detach/attach).
    const attachRef = useCallback(
        (el: HTMLSpanElement | null) => {
            const btn = el ? (el.querySelector('button') as HTMLButtonElement | null) : null;
            setButtonEl(index, btn);
        },
        [index, setButtonEl]
    );

    return (
        <span ref={attachRef} className="contents">
            <FilterDropdown
                title={category.label}
                options={category.options}
                selectedOptions={category.selectedOptions}
                onChange={category.onChange}
                align="right"
                workQueueVariant
                footer={category.footer}
                forceActive={category.forceActive}
            />
        </span>
    );
}
